import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import {
  COMPONENT_KINDS,
  type AircraftComponent,
  type AircraftComponentInput,
  type ComponentKind,
} from '@jupi/shared'
import { ConfirmDialog, api, useClubToday, useSnackbar } from '@jupi/ui'
import { HUOLTO } from '../api'
import { HoursField } from './HoursField'

// Add, edit or remove one tracked part. Its "last done" is on the airframe
// clock, prefilled with the hours now so that a new part starts counting from
// today unless the paperwork says it was done earlier.
export function EquipmentDialog({
  registration,
  airframeMinutes,
  component,
  onClose,
}: {
  registration: string
  airframeMinutes: number | null
  component: AircraftComponent | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const today = useClubToday()
  const [kind, setKind] = useState<ComponentKind>(component?.kind ?? 'equipment')
  const [name, setName] = useState(component?.name ?? '')
  const [interval, setInterval] = useState(component?.intervalHours == null ? '' : String(component.intervalHours))
  const [alertBefore, setAlertBefore] = useState(String(component?.alertBeforeHours ?? 50))
  const [isTbo, setIsTbo] = useState(component?.isTbo ?? false)
  const [lastDone, setLastDone] = useState<number | null>(component?.lastDoneMinutes ?? airframeMinutes ?? 0)
  const [lastDoneOn, setLastDoneOn] = useState(component?.lastDoneOn ?? (component ? '' : today))
  const [confirmRemove, setConfirmRemove] = useState(false)

  const base = `/api/maintenance/${encodeURIComponent(registration)}/components`
  const done = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: HUOLTO })
    showSnackbar(message)
    onClose()
  }
  const failed = () => showSnackbar(t('huolto.saveFailed'), { severity: 'error' })

  const save = useMutation({
    mutationFn: (body: AircraftComponentInput) =>
      component ? api.put(`${base}/${component.componentId}`, body) : api.post(base, body),
    onSuccess: () => done(t('huolto.equipmentSaved')),
    onError: failed,
  })
  const remove = useMutation({
    mutationFn: () => api.delete(`${base}/${component?.componentId}`),
    onSuccess: () => done(t('huolto.equipmentRemoved')),
    onError: failed,
  })

  const intervalHours = interval === '' ? null : Number(interval)
  const alertBeforeHours = alertBefore === '' ? 0 : Number(alertBefore)
  const valid = name.trim() !== '' && lastDone != null && (intervalHours === null || intervalHours >= 1)

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle sx={{ fontSize: '1.5rem' }}>{component ? t('huolto.editEquipment') : t('huolto.addEquipment')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {save.isError ? <Alert severity="error">{t('huolto.saveFailed')}</Alert> : null}
          <ToggleButtonGroup
            exclusive
            fullWidth
            color="primary"
            value={kind}
            onChange={(_, value: ComponentKind | null) => value && setKind(value)}
          >
            {COMPONENT_KINDS.map((k) => (
              <ToggleButton key={k} value={k} sx={{ fontSize: '1.1rem' }}>
                {t(`huolto.kind_${k}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField label={t('huolto.name')} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          <TextField
            label={t('huolto.intervalHours')}
            value={interval}
            onChange={(e) => setInterval(e.target.value.replace(/\D/g, '').slice(0, 5))}
            helperText={t('huolto.intervalHelp')}
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
          />
          <TextField
            label={t('huolto.alertBeforeHours')}
            value={alertBefore}
            onChange={(e) => setAlertBefore(e.target.value.replace(/\D/g, '').slice(0, 5))}
            helperText={t('huolto.alertBeforeHelp')}
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
          />
          <FormControlLabel
            control={<Switch checked={isTbo} onChange={(e) => setIsTbo(e.target.checked)} />}
            label={t('huolto.isTbo')}
          />
          <HoursField label={t('huolto.lastDoneAt')} minutes={lastDone} onChange={setLastDone} />
          <TextField
            type="date"
            label={t('huolto.lastDoneOn')}
            value={lastDoneOn}
            onChange={(e) => setLastDoneOn(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        {component ? (
          <Button color="error" onClick={() => setConfirmRemove(true)}>
            {t('huolto.removeEquipment')}
          </Button>
        ) : (
          <span />
        )}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>{t('huolto.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!valid || save.isPending}
            onClick={() =>
              save.mutate({
                kind,
                name: name.trim(),
                intervalHours,
                lastDoneMinutes: lastDone ?? 0,
                lastDoneOn: lastDoneOn || null,
                notes: component?.notes ?? null,
                alertBeforeHours,
                isTbo,
              })
            }
          >
            {t('common.save')}
          </Button>
        </Stack>
      </DialogActions>
      <ConfirmDialog
        open={confirmRemove}
        title={t('huolto.removeEquipment')}
        message={t('huolto.removeEquipmentConfirm')}
        confirmLabel={t('huolto.removeEquipment')}
        cancelLabel={t('huolto.cancel')}
        onConfirm={() => {
          setConfirmRemove(false)
          remove.mutate()
        }}
        onClose={() => setConfirmRemove(false)}
      />
    </Dialog>
  )
}
